import { useCallback, useEffect, useMemo, useReducer } from "react";

import { loadUser } from "./actions";
import {
  matchChannels,
  reducer,
  sortByLastMessageAt,
  sortByName,
} from "./utils";
import { DM_TYPE, ROOM_TYPE, channelOptsEverything } from "../constants";
import { loadMoreChannelDMs } from "../entities/channels";
import {
  channelsByChannelId,
  initializationState,
  isOnline,
  totalUnreadChannelCount,
  totalUnreadCount,
  usersById,
} from "../state";

export { default as useAutoCompleteUsers } from "./use-auto-complete-users";
export { default as useChannel } from "./use-channel";
export { default as useChannelByUsers } from "./use-channel-by-users";
export { default as useChannelMembers } from "./use-channel-members";
export { default as useChannelMessages } from "./use-channel-messages";
export { default as useMessageInteraction } from "./use-message-interaction";
export { default as useRoomsByUser } from "./use-rooms-by-user";
export { default as useSearchChannelsDm } from "./use-search-channels-dm";

/**
 * exposes: {
 *   error: null | Error,
 *   isInitialized: bool,
 *   stage: "preInit" | "connecting" | "loadingDms" | "loadingRooms" | "ready"
 * }
 * Note: isInitialized === true  and  stage === "ready" are the same thing
 */
export const useInitializedState = () => initializationState.use();

export const useIsSocketConnected = () => isOnline.use();

export const useTotalUnreadCount = () => totalUnreadCount.use();

export const useTotalUnreadChannelCount = () => totalUnreadChannelCount.use();

// loads a list of rooms that the user is a member of
export function useChannelRooms() {
  const obj = channelsByChannelId.use();
  return useMemo(() => {
    const channels = matchChannels(
      obj,
      (c) => c.type === ROOM_TYPE && c.isMember === true
    );
    channels.sort(sortByLastMessageAt);
    return {
      channels,
      loading: false, // this becomes more useful if we add a fetchMore function
      error: null, // this becomes more useful if add async functions like fetchMore
    };
  }, [obj]);
}

// loads a list of all DMs the user is a member of
export function useChannelDMs() {
  const obj = channelsByChannelId.use();

  const [state, dispatch] = useReducer(reducer, {
    channels: [],
    loading: false,
    error: null,
    hasMore: false,
  });

  const memoChannels = useMemo(() => {
    const channels = matchChannels(
      obj,
      (c) => c.type === DM_TYPE && c.isMember === true
    );
    channels.sort(sortByLastMessageAt);
    return channels;
  }, [obj]);

  useEffect(() => {
    dispatch({
      channels: memoChannels,
      error: null,
      loading: false,
      hasMore: memoChannels.length === channelOptsEverything.limit,
    });
  }, [memoChannels]);

  const loadMore = useCallback(async () => {
    dispatch({ error: null, loading: true });
    try {
      const { hasMore } = await loadMoreChannelDMs(memoChannels.length);
      dispatch({ hasMore });
    } catch (error) {
      dispatch({ error, loading: false });
    }
  }, [memoChannels]);

  return {
    ...state,
    loadMore,
  };
}

// loads all rooms regardless of membership
export function useChannelRoomsAll() {
  const obj = channelsByChannelId.use();
  return useMemo(() => {
    const channels = matchChannels(obj, (c) => c.type === ROOM_TYPE);
    channels.sort(sortByName);
    return {
      channels,
      loading: false, // this becomes more useful if we add a fetchMore function
      error: null, // this becomes more useful if add async functions like fetchMore
    };
  }, [obj]);
}

function createStubUser(userId) {
  return {
    userId,
    name: "",
    firstName: "",
    company: "",
    avatar: null,
    location: "",
    title: "",
    role: "MEMBER",
    isOnline: false,
    loading: true,
    pronouns: "",
    displayPronouns: false,
  };
}

export function useChannelUser({ userId }) {
  const user = usersById.use((obj) => obj[userId]);

  return useMemo(() => {
    if (user) {
      return user;
    }

    loadUser(userId);

    return createStubUser(userId);
  }, [userId, user]);
}
